
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MailCheck, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import supabase from "../utils/supabaseClient";
import {SEO} from "@/components/SEO.tsx";

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<"loading" | "success" | "expired" | "error">("loading");

  useEffect(() => {

    const callApi = async (token: string, email: string, redirectTo: string) =>{
      try {
        const url = `${import.meta.env.VITE_API_URL}/users/verification/verify`;
        const headers = {
          'Content-Type': 'application/json',
        };
        const body = JSON.stringify({email: email, token: token, type: "signup", options: {redirectTo}});

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body,
        });

        const responseData = await response.json();
        // console.log("responseData:",responseData)
        if (responseData.data) {
          return {success: true, message: null}
        } else {
          return {success: false, message: responseData.error}
        }
      } catch (error) {
        // console.error('Error:', error);
        return {success: false, message: error.message}
      }
    }
    const verifyEmail = async () => {
      try {
        const token = searchParams.get("token");
        const email = searchParams.get("email");
        const redirectTo = searchParams.get("redirectTo");

        if (!token || !email) {
          setVerificationStatus("error");
          return;
        }

        const {success, message} = await callApi(token, email, redirectTo)

        // console.log("success:",success)
        // console.log("message:",message)

        if (!success) {
          if (message?.includes("expired")) {
            setVerificationStatus("expired");
          } else {
            setVerificationStatus("error");
          }
          return;
        }

        setVerificationStatus("success");
        // 5 saniye sonra login sayfasına yönlendir
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      } catch (error) {
        setVerificationStatus("error");
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResendVerification = async () => {
    setIsLoading(true);
    try {
      const email = searchParams.get("email");
      if (!email) {
        toast.error("Email address not found");
        return;
      }

      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
      });

      if (error) throw error;

      return toast.success("Verification email has been resent");
    } catch (error) {
      return toast.error("Failed to resend verification email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEO
          title="Email Confirmation"
          description="Confirm your email address to activate your account"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <div className="container max-w-md mx-auto pt-24 px-4">
        <div className="bg-white p-8 rounded-lg border border-gray-200">
          {verificationStatus === "loading" && (
            <div className="text-center">
              <RefreshCw className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-semibold mb-2">Verifying Email</h2>
              <p className="text-gray-600">Please wait while we verify your email address...</p>
            </div>
          )}

          {verificationStatus === "success" && (
            <div className="text-center">
              <MailCheck className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-2">Email Verified!</h2>
              <p className="text-gray-600">You will be redirected to the login page in 5 seconds...</p>
            </div>
          )}

          {verificationStatus === "expired" && (
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-2">Verification Link Expired</h2>
              <p className="text-gray-600 mb-6">The verification link has expired. Please request a new one.</p>
              <Button
                onClick={handleResendVerification}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Resend Verification Email"
                )}
              </Button>
            </div>
          )}

          {verificationStatus === "error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                An error occurred during email verification. Please try again or contact support.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmation;
